import React from 'react';

import { type Action } from '../../../common/types';
import { fg } from '../../../controllers/feature-gates';
import { settingsCodePlannerPath } from '../../../routes/paths';
import {
	CODE_PLANNER_LEARN_MORE_LINK,
	CODE_REVIEWER_LEARN_MORE_LINK,
	CODE_SEARCH_LEARN_MORE_LINK,
	CODING_AGENTS_LEARN_MORE_LINK,
	DEPLOYMENT_SUMMARIZER_LEARN_MORE_LINK,
	DEPLOYMENT_SUMMARIZER_SETTINGS_LINK,
	JOIN_WAITLIST_LINK,
	PIPELINE_TROUBLESHOOTER_LEARN_MORE_LINK,
	PIPELINE_TROUBLESHOOTER_SETTINGS_LINK,
} from '../../../utils/constants';
import { CodeReviewerSettings } from '../code-reviewer-settings';
import {
	CodePlannerIcon,
	CodeReviewerIcon,
	CodingAgentsIcon,
	DeploymentSummarizerIcon,
	PipelineAnalyzerIcon,
	RovoSearchIcon,
} from '../common/icons';

export type Capability = {
	title: string;
	description: string;
	icon: React.ReactNode;
	primaryAction: Action | undefined;
	secondaryAction?: Action | undefined;
	lozenge?: {
		text: string;
		appearance?: 'default' | 'inprogress' | 'moved' | 'new' | 'removed' | 'success';
		isBold?: boolean;
	};
	testId?: string;
	priority: number;
	isAvailable: boolean;
};

const getCodingAgentsLearnMoreAction = (): Action => {
	return {
		label: 'Read about coding agents',

		href: {
			link: CODING_AGENTS_LEARN_MORE_LINK,
		},
		analyticEvent: {
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'coding-agents-info-link',
			source: 'home-page-agents',
		},
	};
};

const getCodingAgentsPrimaryAction = (): Action => {
	if (fg('coding-agents-limited-beta')) {
		return getCodingAgentsLearnMoreAction(); // TODO: Explore agents link is not yet ready
	}
	return {
		label: 'Join waitlist',
		href: {
			link: JOIN_WAITLIST_LINK,
			target: '_blank',
		},
		analyticEvent: {
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'coding-agents-waitlist',
			source: 'home-page-agents',
		},
	};
};

// TODO: Explore agents link is not yet ready
const getCodingAgentsSecondaryAction = (): Action | undefined => {
	if (fg('coding-agents-limited-beta')) {
		return undefined;
	}
	return getCodingAgentsLearnMoreAction();
};

// TODO: Explore agents link is not yet ready
// const getCodingAgentsSettingsAction = (): Action => {
// 	return {
// 		label: 'Explore agents',

// 		href: {
// 			link: '',
// 		},
// 	};
// };

const getCodePlannerLearnMoreAction = (): Action => {
	return {
		label: 'Read about Code Planner',
		href: {
			link: CODE_PLANNER_LEARN_MORE_LINK,
		},
		analyticEvent: {
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'code-planner-info-link',
			source: 'home-page-agents',
		},
	};
};

const getCodePlannerPrimaryAction = (): Action => {
	if (fg('code-planner-agent-limited-beta')) {
		return {
			label: 'Enable Code Planner on projects',
			href: { link: settingsCodePlannerPath, target: '_self' },
			analyticEvent: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'code-planner-settings',
				source: 'home-page-agents',
			},
		};
	}
	return {
		label: 'Join waitlist',
		href: { link: JOIN_WAITLIST_LINK, target: '_blank' },
		analyticEvent: {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'code-planner-settings-waitlist',
			source: 'home-page-agents',
		},
		testId: 'enable-code-planner',
	};
};

const getCodeReviewerLearnMoreAction = (): Action => {
	return {
		label: 'Read about Code Reviewer',
		href: {
			link: CODE_REVIEWER_LEARN_MORE_LINK,
		},
		analyticEvent: {
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'code-reviewer-info-link',
			source: 'home-page-agents',
		},
	};
};

const getCodeReviewerSettingsAction = (showBitbucket: boolean, showGitHub: boolean): Action => {
	return {
		label: 'Enable Code Reviewer on repositories',
		children: <CodeReviewerSettings showBitbucket={showBitbucket} showGitHub={showGitHub} />,
	};
};

const getDeploymentSummarizerLearnMoreAction = (): Action => {
	return {
		label: 'Read about Deployment Summarizer',
		href: {
			link: DEPLOYMENT_SUMMARIZER_LEARN_MORE_LINK,
		},
		analyticEvent: {
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'deployment-summarizer-info-link',
			source: 'home-page-agents',
		},
	};
};

const getDeploymentSummarizeSettingsAction = () => {
	return {
		label: 'Set up Deployment Summarizer',
		href: {
			link: DEPLOYMENT_SUMMARIZER_SETTINGS_LINK,
		},
		analyticEvent: {
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'deployment-summarizer-settings',
			source: 'home-page-agents',
		},
	};
};

const getPipelineTroubleshooterLearnMoreAction = (): Action => {
	return {
		label: 'Read about Pipeline Troubleshooter',
		href: {
			link: PIPELINE_TROUBLESHOOTER_LEARN_MORE_LINK,
		},
		analyticEvent: {
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'pipeline-troubleshooter-info-link',
			source: 'home-page-agents',
		},
	};
};

const getPipelineTroubleshooterSettingsAction = (): Action => {
	return {
		label: 'Set up Pipeline Troubleshooter',
		href: {
			link: PIPELINE_TROUBLESHOOTER_SETTINGS_LINK,
		},
		analyticEvent: {
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'pipeline-troubleshooter-settings',
			source: 'home-page-agents',
		},
	};
};

const getCodeReviewerPrimaryAction = (
	isUserProductAdmin: boolean,
	hasScmIntegrations: boolean,
	isBitbucketConnected: boolean,
	isGitHubConnected: boolean,
) => {
	if (!isUserProductAdmin) {
		return getCodeReviewerLearnMoreAction();
	}
	return hasScmIntegrations
		? getCodeReviewerSettingsAction(isBitbucketConnected, isGitHubConnected)
		: getCodeReviewerLearnMoreAction();
};

export const getCapabilities = (
	isUserProductAdmin: boolean,
	hasScmIntegrations: boolean,
	isBitbucketConnected: boolean,
	isGitHubConnected: boolean,
): Capability[] => {
	return [
		{
			title: 'Code Planner',
			description: 'Writes a coding plan based on a work item.',
			icon: <CodePlannerIcon />,
			primaryAction: isUserProductAdmin
				? getCodePlannerPrimaryAction()
				: getCodePlannerLearnMoreAction(),
			secondaryAction: isUserProductAdmin ? getCodePlannerLearnMoreAction() : undefined,
			testId: 'code-planner',
			priority: 1,
			isAvailable: fg('code-planner-agent-limited-beta'),
		},
		{
			title: 'Coding Agents',
			description: 'Generate code to complete tasks.',
			icon: <CodingAgentsIcon />,
			primaryAction: isUserProductAdmin
				? getCodingAgentsPrimaryAction()
				: getCodingAgentsLearnMoreAction(),
			secondaryAction: isUserProductAdmin ? getCodingAgentsSecondaryAction() : undefined,
			testId: 'coding-agents',
			priority: 2,
			isAvailable: fg('coding-agents-limited-beta'),
		},
		{
			title: 'Code Reviewer',
			description: 'Reads pull requests and adds feedback.',
			icon: <CodeReviewerIcon />,
			primaryAction: getCodeReviewerPrimaryAction(
				isUserProductAdmin,
				hasScmIntegrations,
				isBitbucketConnected,
				isGitHubConnected,
			),
			secondaryAction:
				isUserProductAdmin && hasScmIntegrations ? getCodeReviewerLearnMoreAction() : undefined,
			testId: 'code-reviewer',
			priority: 3,
			isAvailable: true,
		},
		{
			title: 'Pipeline Troubleshooter',
			lozenge: { text: 'Bitbucket only', appearance: 'default', isBold: true },
			description: 'Helps identify and fix pipeline step failures.',
			icon: <PipelineAnalyzerIcon />,
			primaryAction:
				isUserProductAdmin && isBitbucketConnected
					? getPipelineTroubleshooterSettingsAction()
					: getPipelineTroubleshooterLearnMoreAction(),
			secondaryAction:
				isUserProductAdmin && isBitbucketConnected
					? getPipelineTroubleshooterLearnMoreAction()
					: undefined,
			testId: 'pipeline-troubleshooter',
			priority: 4,
			isAvailable: true,
		},
		{
			title: 'Deployment Summarizer',
			lozenge: { text: 'Bitbucket only', appearance: 'default', isBold: true },
			description: 'Summarizes all changes in a deployment.',
			icon: <DeploymentSummarizerIcon />,
			primaryAction:
				isUserProductAdmin && isBitbucketConnected
					? getDeploymentSummarizeSettingsAction()
					: getDeploymentSummarizerLearnMoreAction(),
			secondaryAction:
				isUserProductAdmin && isBitbucketConnected
					? getDeploymentSummarizerLearnMoreAction()
					: undefined,
			testId: 'deployment-summarizer',
			priority: 5,
			isAvailable: true,
		},
		{
			title: 'Code search in Rovo chat',
			description: 'Search your codebase using Rovo chat.',
			icon: <RovoSearchIcon />,
			primaryAction: {
				label: 'About code search in Rovo chat',
				href: {
					link: CODE_SEARCH_LEARN_MORE_LINK,
				},
				analyticEvent: {
					action: 'clicked',
					actionSubject: 'link',
					actionSubjectId: 'code-search-info-link',
					source: 'home-page-agents',
				},
			},
			testId: 'code-search',
			priority: 6,
			isAvailable: false,
		},
	];
};
